// schema-version — single source of truth for IngestPackage / CourseDraft
// schema versioning. Per LLM_NATIVE_ARCHITECTURE.md §2.4 / §2.5.
//
// Why a versioned schema:
//   LLM-native products evolve schema 10× faster than traditional SaaS — every
//   prompt iteration may want a new field, a refined enum, a renamed shape.
//   Old IngestPackage produced before today's schema must keep loading; new
//   views must declare what minimum version they require, not crash on absence.
//
// Versioning rules:
//   - bump PATCH (0.1.0 → 0.1.1) for additive optional field changes
//   - bump MINOR (0.1.x → 0.2.0) for additive enum values, new optional sections
//   - bump MAJOR (0.x.x → 1.0.0) for breaking changes (renames, type changes,
//     removed fields). Requires DEVLOG sign-off.
//
// Passthrough policy (§2.4):
//   All schemas in this directory should call `.passthrough()` on their top-
//   level z.object so unknown fields produced by the LLM are preserved
//   verbatim — not silently stripped. The default zod behavior is `strip`,
//   which loses information when the LLM emits extra fields. We prefer
//   passthrough so future readers can recover unknown content (and so a newer
//   producer can write fields an older consumer doesn't yet understand).
/** Current IngestPackage schema version.
 *
 *  Version log:
 *    0.1.0 (19AL) — initial; passthrough + sourceVersion field added
 *    0.2.0 (19AO) — content-quality constraint bump:
 *                   mcq.explanation min 80 / question min 20
 *                   tf.statement min 20 / explanation min 60
 *                   flashcard.front min 15 / back min 40
 *                   glossary.shortDef min 20 / longDef min 80
 *                   essay.modelAnswer min 200 / keyPoints min 3 / scaffold min 3
 *                   lecture_notes.content min 300 / summary min 40 / title min 8
 *                   mindmap.nodes min 5 / label min 3
 *                   proof_drill.keySteps min 3 / prompt min 30
 *                   case.subtitle min 15
 *                   free_response.referenceAnswer / reasoning min 80
 *                   reading_passage.passage min 600
 *                   + new freeResponses[], readingPassages[] top-level arrays
 *                   Salvage path may drop legacy items below new floors.
 *    0.3.0 (19BH Day 5 Step 5.1) — v4 anchor-first ingest wave (MINOR · additive):
 *                   + ItemTags universal (caseIds/topicIds/conceptIds[] on every
 *                     LLM-generated item · z.preprocess coerces v3 singular)
 *                   + omittedConcepts? optional on subjective items (audit hook)
 *                   + curriculumOutline? optional (frozen anchor tree)
 *                   + gapReport? optional (no-LLM audit Phase 4 output)
 *                   Migration (migrate-legacy.ts v3→v4 entries + idb.ts
 *                   DB_VERSION 4→5 onupgradeneeded) wraps old data forward;
 *                   singular caseId/topicId/conceptTag preserved as alias for
 *                   19BH+1 frontend back-compat (removed in 19BI).
 *    0.3.1 (19BO+ P2-1) — additive optional `examType?: string` field (PATCH).
 *                   Persisted so regen + route_discipline can read the original
 *                   exam scenario. Loose string (not enum) so new disciplines'
 *                   exam types don't require another bump.
 */
export const INGEST_PACKAGE_SCHEMA_VERSION = '0.3.1';
/** Current CourseDraft schema version. Independent from IngestPackage version.
 *
 *  Version log:
 *    0.1.0 (19A)   — initial
 *    0.3.0 (19BH Day 1 Step 1.6) — v4 anchor-first ingest wave: added
 *                    optional `curriculumOutline: CurriculumOutline` field.
 *                    NOTE: spec/plan referred to this version label as 'v4' for
 *                    architectural shorthand; we land it as semver '0.3.0' (MINOR
 *                    bump · additive optional field per §rules above) so
 *                    compareSemver / meetsMinSchemaVersion continue to work.
 *    0.3.1 (19BO+ P2-1) — additive optional `examType?: string` field (PATCH).
 *                   Drives route_discipline stage scenario selection. Loose
 *                   string schema; per-discipline options in EXAM_TYPES_BY_DISCIPLINE.
 */
export const COURSE_DRAFT_SCHEMA_VERSION = '0.3.1';
/** Current quality-rubric version (Sprint 19BG+1 Phase 4 EVAL-ANNOTATION-UI).
 *
 *  The rubric defines the dimensions humans score AI output on (fidelity /
 *  dispersion / fictional / cog) plus the 0-3 anchor descriptions. Annotations
 *  produced under this rubric carry `rubricVersion` so future rubric edits
 *  (new dimension, renamed anchor) can co-exist with old data via the lenient
 *  `meetsMinSchemaVersion` policy — old annotations remain readable, new views
 *  filter or display "vintage" badge.
 *
 *  Version log:
 *    1.0.0 (19BG+1) — initial 4-dim rubric; scores 0-3; passthrough fields
 *                    `flags[]` / `comment` / `reviewerId` / `runId`.
 */
export const QUALITY_RUBRIC_VERSION = '1.0.0';
/** Semver comparator: returns -1 / 0 / 1. Strict triple-segment, no prerelease.
 *  Throws on malformed input — callers should pre-validate via `isValidSemver`. */
export function compareSemver(a, b) {
    const parse = (v) => {
        const m = v.match(/^(\d+)\.(\d+)\.(\d+)$/);
        if (!m)
            throw new Error(`invalid semver: ${v}`);
        return [Number(m[1]), Number(m[2]), Number(m[3])];
    };
    const [aMaj, aMin, aPat] = parse(a);
    const [bMaj, bMin, bPat] = parse(b);
    if (aMaj !== bMaj)
        return aMaj < bMaj ? -1 : 1;
    if (aMin !== bMin)
        return aMin < bMin ? -1 : 1;
    if (aPat !== bPat)
        return aPat < bPat ? -1 : 1;
    return 0;
}
/** True iff `actual >= required`. Lenient: missing/malformed `actual` returns
 *  false rather than throwing — older artefacts with no schemaVersion are
 *  considered "below any required version" by design. */
export function meetsMinSchemaVersion(actual, required) {
    if (!actual)
        return false;
    try {
        return compareSemver(actual, required) >= 0;
    }
    catch {
        return false;
    }
}
/** Validates `x.y.z` shape. */
export function isValidSemver(v) {
    return /^\d+\.\d+\.\d+$/.test(v);
}
//# sourceMappingURL=schema-version.js.map